import { create } from 'zustand'
import type { UsersAndContacts } from '@/types/contacts'

interface SelectedRecipient {
  id: string
  name: string
  username?: string
  avatar?: string
  phoneNumber?: string
  avatarBg?: [string, string, ...string[]]
}

interface MultipleRecipientsState {
  selectedRecipients: SelectedRecipient[]
  maxRecipients: number
  
  // Actions
  addRecipient: (recipient: UsersAndContacts) => void
  removeRecipient: (recipientId: string) => void
  toggleRecipient: (recipient: UsersAndContacts) => void
  isRecipientSelected: (recipientId: string) => boolean
  clearSelection: () => void
  canAddMore: () => boolean
}

export const useMultipleRecipientsStore = create<MultipleRecipientsState>(
  (set, get) => ({
    selectedRecipients: [],
    maxRecipients: 10,

    addRecipient: (recipient: UsersAndContacts) => {
      const { selectedRecipients, maxRecipients } = get()
      
      // Check if already selected
      if (selectedRecipients.some((r) => r.id === recipient.id)) {
        return
      }
      
      // Check max limit
      if (selectedRecipients.length >= maxRecipients) {
        return
      }

      const newRecipient: SelectedRecipient = {
        id: recipient.id,
        name: `${recipient.first_name} ${recipient.last_name}`.trim(),
        username: recipient.username,
        avatar: recipient.avatar_path,
        phoneNumber: recipient.phone_number,
        avatarBg: recipient.avatarBg,
      }

      set({
        selectedRecipients: [...selectedRecipients, newRecipient],
      })
    },

    removeRecipient: (recipientId: string) => {
      set((state) => ({
        selectedRecipients: state.selectedRecipients.filter(
          (r) => r.id !== recipientId,
        ),
      }))
    },

    toggleRecipient: (recipient: UsersAndContacts) => {
      const { selectedRecipients } = get()
      const isSelected = selectedRecipients.some((r) => r.id === recipient.id)

      if (isSelected) {
        get().removeRecipient(recipient.id)
      } else {
        get().addRecipient(recipient)
      }
    },

    isRecipientSelected: (recipientId: string) => {
      return get().selectedRecipients.some((r) => r.id === recipientId)
    },

    clearSelection: () => {
      set({ selectedRecipients: [] })
    },

    canAddMore: () => {
      const { selectedRecipients, maxRecipients } = get()
      return selectedRecipients.length < maxRecipients
    },
  }),
)

